import { useState, useEffect, useCallback, type RefObject } from 'react';

/**
 * Detects whether a scrollable element has horizontal overflow to the right
 * and returns a boolean that can drive a gradient / shadow indicator.
 */
export const useScrollShadow = (scrollRef: RefObject<HTMLElement | null>) => {
    const [showRightShadow, setShowRightShadow] = useState(false);

    const check = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            setShowRightShadow(false);
            return;
        }
        const hasOverflow = el.scrollWidth > el.clientWidth + 1; // 1px tolerance
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
        setShowRightShadow(hasOverflow && !atEnd);
    }, [scrollRef]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        // Initial check
        check();

        // Re-check on scroll
        el.addEventListener('scroll', check, { passive: true });

        // Re-check when the element resizes (e.g. window resize, sidebar toggle)
        const observer = new ResizeObserver(check);
        observer.observe(el);

        return () => {
            el.removeEventListener('scroll', check);
            observer.disconnect();
        };
    }, [scrollRef, check]);

    return { showRightShadow };
};
